import type { BoardContent, DifficultyParams } from '../core';

export const NOVICE_BOARD_COUNT = 3;
export const NOVICE_TOPIC_CATEGORY = 'training';

// The first training board hides its timer bar (GameScene timerVisible=false for novice board 0),
// but Core still ends a board 'lost' when timeLeftMs hits 0 — so this must be long enough that a
// hidden timer can NEVER silently lose a first-timer (incl. a curious child) on the very first
// teaching board. 30 min is effectively untimed for a 4-tile "eat the evens" board.
const LONG_UNTIMED_BOARD_MS = 1_800_000;

const NOVICE_BOARDS: BoardContent[] = [
  {
    // 2026-07-01 hook tune (re-score: "board 1 is a bare dark 4×4 of numbers, zero topic flavor").
    // The muncher's fantasy is EATING — the first rule a new player ever reads should be about food,
    // not parity math. Items chosen to be universally known + machine-checkable with zero contest
    // (no tomato/cucumber botanical traps).
    topicRaw: 'Training: Fruit',
    topicNormalized: 'training-fruit',
    ruleStatement: 'Eat only fruit.',
    rows: 4,
    cols: 4,
    source: 'seed',
    verified: true,
    cells: [
      { text: 'apple', kind: 'correct', difficulty: 1, explanation: 'An apple is a fruit.' },
      { text: 'banana', kind: 'correct', difficulty: 1, explanation: 'A banana is a fruit.' },
      { text: 'mango', kind: 'correct', difficulty: 1, explanation: 'A mango is a fruit.' },
      { text: 'pear', kind: 'correct', difficulty: 1, explanation: 'A pear is a fruit.' },
      { text: 'carrot', kind: 'decoy', difficulty: 1, explanation: 'A carrot is a root vegetable.' },
      { text: 'potato', kind: 'decoy', difficulty: 1, explanation: 'A potato is a vegetable.' },
      { text: 'onion', kind: 'decoy', difficulty: 1, explanation: 'An onion is a vegetable.' },
      { text: 'bread', kind: 'decoy', difficulty: 1, explanation: 'Bread is baked, not grown.' },
      { text: 'cheese', kind: 'decoy', difficulty: 1, explanation: 'Cheese is made from milk.' },
      { text: 'pizza', kind: 'decoy', difficulty: 1, explanation: 'Pizza is a dish, not a fruit.' },
      { text: 'rock', kind: 'decoy', difficulty: 1, explanation: 'A rock is not food at all.' },
      { text: 'sock', kind: 'decoy', difficulty: 1, explanation: 'A sock is clothing — do not eat it.' },
      { text: 'chair', kind: 'decoy', difficulty: 1, explanation: 'A chair is furniture.' },
      { text: 'cloud', kind: 'decoy', difficulty: 1, explanation: 'A cloud is weather, not food.' },
      { text: 'spoon', kind: 'decoy', difficulty: 1, explanation: 'A spoon is a utensil.' },
      { text: 'boot', kind: 'decoy', difficulty: 1, explanation: 'A boot is footwear.' },
    ],
  },
  {
    topicRaw: 'Training: Flying Animals',
    topicNormalized: 'training-flying-animals',
    ruleStatement: 'Eat only animals that fly.',
    rows: 4,
    cols: 4,
    source: 'seed',
    verified: true,
    cells: [
      { text: 'bat', kind: 'correct', difficulty: 1, explanation: 'Bats are mammals that fly.' },
      { text: 'owl', kind: 'correct', difficulty: 1, explanation: 'Owls are flying birds.' },
      { text: 'bee', kind: 'correct', difficulty: 1, explanation: 'Bees fly.' },
      { text: 'swan', kind: 'correct', difficulty: 1, explanation: 'Swans are birds that fly.' },
      { text: 'gull', kind: 'correct', difficulty: 1, explanation: 'Gulls are flying birds.' },
      { text: 'cat', kind: 'decoy', difficulty: 1, explanation: 'Cats do not fly.' },
      { text: 'dog', kind: 'decoy', difficulty: 1, explanation: 'Dogs do not fly.' },
      { text: 'frog', kind: 'decoy', difficulty: 1, explanation: 'Frogs jump, but they do not fly.' },
      { text: 'fish', kind: 'decoy', difficulty: 1, explanation: 'Fish swim.' },
      { text: 'horse', kind: 'decoy', difficulty: 1, explanation: 'Horses run, not fly.' },
      { text: 'snake', kind: 'decoy', difficulty: 1, explanation: 'Snakes crawl.' },
      { text: 'crab', kind: 'decoy', difficulty: 1, explanation: 'Crabs walk and swim.' },
      { text: 'cow', kind: 'decoy', difficulty: 1, explanation: 'Cows do not fly.' },
      { text: 'seal', kind: 'decoy', difficulty: 1, explanation: 'Seals swim.' },
      { text: 'goat', kind: 'decoy', difficulty: 1, explanation: 'Goats climb, but do not fly.' },
      { text: 'ant', kind: 'decoy', difficulty: 1, explanation: 'Worker ants do not fly.' },
    ],
  },
  {
    topicRaw: 'Training: Planets',
    topicNormalized: 'training-planets',
    ruleStatement: 'Eat only planets.',
    rows: 4,
    cols: 5,
    source: 'seed',
    verified: true,
    cells: [
      { text: 'Mars', kind: 'correct', difficulty: 1, explanation: 'Mars is a planet.' },
      { text: 'Venus', kind: 'correct', difficulty: 1, explanation: 'Venus is a planet.' },
      { text: 'Earth', kind: 'correct', difficulty: 1, explanation: 'Earth is a planet.' },
      { text: 'Jupiter', kind: 'correct', difficulty: 1, explanation: 'Jupiter is a planet.' },
      { text: 'Saturn', kind: 'correct', difficulty: 1, explanation: 'Saturn is a planet.' },
      { text: 'Neptune', kind: 'correct', difficulty: 1, explanation: 'Neptune is a planet.' },
      { text: 'Sun', kind: 'decoy', difficulty: 1, explanation: 'The Sun is a star.' },
      { text: 'Moon', kind: 'decoy', difficulty: 1, explanation: 'The Moon is a natural satellite.' },
      { text: 'Comet', kind: 'decoy', difficulty: 1, explanation: 'A comet is not a planet.' },
      { text: 'Asteroid', kind: 'decoy', difficulty: 1, explanation: 'An asteroid is not a planet.' },
      { text: 'Galaxy', kind: 'decoy', difficulty: 1, explanation: 'A galaxy contains stars and planets.' },
      { text: 'Meteor', kind: 'decoy', difficulty: 1, explanation: 'A meteor is not a planet.' },
      { text: 'Nebula', kind: 'decoy', difficulty: 1, explanation: 'A nebula is a cloud of gas and dust.' },
      { text: 'Orbit', kind: 'decoy', difficulty: 1, explanation: 'An orbit is a path, not a planet.' },
      { text: 'Rocket', kind: 'decoy', difficulty: 1, explanation: 'A rocket is a vehicle.' },
      { text: 'Telescope', kind: 'decoy', difficulty: 1, explanation: 'A telescope is a tool.' },
      { text: 'Crater', kind: 'decoy', difficulty: 1, explanation: 'A crater is a surface feature.' },
      { text: 'Eclipse', kind: 'decoy', difficulty: 1, explanation: 'An eclipse is an event.' },
      { text: 'Gravity', kind: 'decoy', difficulty: 1, explanation: 'Gravity is a force.' },
      { text: 'Star', kind: 'decoy', difficulty: 1, explanation: 'A star is not a planet.' },
    ],
  },
];

const NOVICE_PARAMS: DifficultyParams[] = [
  {
    rows: 4,
    cols: 4,
    walls: 0,
    targetCorrect: 4,
    quota: 4,
    fenceRatio: 0,
    softTimerMs: LONG_UNTIMED_BOARD_MS,
    hazardTypes: [],
    hazardIntervalMs: 60_000,
    aiTier: 'rookie',
  },
  {
    rows: 4,
    cols: 4,
    walls: 0,
    targetCorrect: 5,
    quota: 4,
    fenceRatio: 0.45,
    softTimerMs: 60_000,
    hazardTypes: [],
    hazardIntervalMs: 60_000,
    aiTier: 'rookie',
  },
  {
    rows: 4,
    cols: 5,
    walls: 0,
    targetCorrect: 6,
    quota: 5,
    fenceRatio: 0.12,
    softTimerMs: 65_000,
    hazardTypes: [],
    hazardIntervalMs: 60_000,
    aiTier: 'rookie',
  },
];

// 9+ punch #37 — pre-vetted board-1 (the fenced training board) Sim seeds. Each value, fed to
// `new Rng(seed).fork(101)` → buildBoard(noviceBoardFor(1), noviceDifficultyForBoard(1)), places ≥1
// "fenced correct" (an interior correct whose every passable orthogonal neighbor is a decoy — the ONLY
// way onto it is off a decoy you arm on entry, forcing a threaded chomp). The default match-seed
// derivation misses this on ~44% of seeds (0 fenced at the shipped default seed 11), so the mandatory
// teaching path could silently never present the Gauntlet. These are guaranteed by scripts/smoke/
// novicefence.ts — regenerate + re-vet there if the board-1 content/params ever change. Render-side
// only: this is the Sim seed input, never a src/core edit. (Each listed seed currently fences ≥2.)
export const NOVICE_B1_FENCED_SEEDS: readonly number[] = [1, 15, 17, 19, 21, 23, 24, 34];

/**
 * Deterministic per-board Sim seed for the novice run. Board 1 (the fenced training board) draws from
 * the pre-vetted NOVICE_B1_FENCED_SEEDS so it ALWAYS presents a fenced correct (the threading lesson on
 * the mandatory path); the choice is stable + varied by the match seed so `?seed=` still reproduces.
 * Every other novice board keeps the standard `matchSeed ^ imul(boardIndex+1, …)` derivation used
 * game-wide (byte-identical to the previous inline expression).
 */
export function noviceBoardSeed(boardIndex: number, matchSeed: number): number {
  if (boardIndex === 1) {
    const list = NOVICE_B1_FENCED_SEEDS;
    return (list[(matchSeed >>> 0) % list.length] as number) >>> 0;
  }
  return (matchSeed ^ Math.imul(boardIndex + 1, 0x9e3779b1)) >>> 0;
}

export function noviceBoardFor(boardIndex: number): BoardContent {
  const i = Math.max(0, Math.min(NOVICE_BOARDS.length - 1, boardIndex));
  return NOVICE_BOARDS[i] as BoardContent;
}

export function noviceDifficultyForBoard(boardIndex: number): DifficultyParams {
  const i = Math.max(0, Math.min(NOVICE_PARAMS.length - 1, boardIndex));
  return NOVICE_PARAMS[i] as DifficultyParams;
}

export function noviceAiEnabled(boardIndex: number): boolean {
  return boardIndex >= 2;
}

export function isFinalNoviceBoard(boardIndex: number): boolean {
  return boardIndex >= NOVICE_BOARD_COUNT - 1;
}

export function isNoviceScanUnlocked(boardIndex: number): boolean {
  return boardIndex >= 1;
}

export function noviceCoachForBoard(boardIndex: number): { title: string; hint: string } {
  if (boardIndex <= 0) {
    return {
      title: 'TRAINING 1',
      hint: "Move to an answer. Tap CHOMP when you're sure.",
    };
  }
  if (boardIndex === 1) {
    // 9+ punch #37 — teach the SOUL by doing on the mandatory path (not just in the optional "?"):
    // this board is fenced (params fenceRatio 0.45), so name threading here so the player routes
    // THROUGH a decoy onto a correct and earns the real THREADED IT ×1.5 in-board.
    return {
      title: 'TRAINING 2',
      hint: 'Wrong tiles are safe to cross — thread THROUGH them onto the right answer for a Gauntlet bonus.',
    };
  }
  // 9+ punch #19 — the rival's JOIN board. The coach line is pure villain-join flavor so it no longer
  // DUPLICATES the pre-round card's auto "beat rival to correct answers" instruction line (the old
  // "Beat the rival to five correct answers." said the same thing twice). The title doubles as the
  // "RIVAL INCOMING" beat in copy; the 500ms animated entry is a GameScene hook (reported cross-file).
  return {
    title: 'RIVAL INCOMING',
    hint: 'BYT3 has entered the board — time to prove it.',
  };
}
